import { supabase } from '../../api/supabase'
import { normalizeCustomerFacets, type AddressFacets } from './normalize'
import { docFlagsOf, pickParty } from '../../../supabase/functions/_shared/partyScope.ts'

interface JobBundleJob {
  id: string
  job_ref: string
  transport_mode: string
  job_type: string
  parent_job_id: string | null
  importer_id: string | null
  consignor_id: string | null
  hbl_importer_id: string | null
  hbl_consignor_id: string | null
}

export interface JobBundle {
  primaryJob: JobBundleJob
  // job_id(s) whose job_goods rows get summed for totals/commodities.
  goodsJobIds: string[]
  isConsolBundle: boolean
  childJobRefs: string[]
}

export interface JobVerificationData {
  job: { id: string; job_ref: string; transport_mode: string }
  consignor: AddressFacets | null
  importer: AddressFacets | null
  shippingTermsCode: string | null
  jobGoods: Array<{ goods_description: string | null; no_of_packs: number | null; gross_weight: number | null }>
  // K10: cbm is null (not 0) when no job_goods row for this bundle has a volume_m3
  // recorded at all -- distinguishes "not captured" from "genuinely zero".
  totals: { packages: number; grossWeight: number; cbm: number | null }
}

// Structured customer columns are only the fallback: customer_addresses wins where a typed address exists
// (Task O) -- see ADDRESS_COLUMNS/pickOperationsAddress below.
const CUSTOMER_COLUMNS = 'id, name, address_line_1, address_line_2, city, postcode'

interface CustomerQueryRow {
  id: string
  name: string
  address: string | null
  address_line_1: string | null
  address_line_2: string | null
  city: string | null
  postcode: string | null
}

const ADDRESS_COLUMNS = 'customer_id, is_default, address_lines, city, county, postcode, country'

interface CustomerAddressQueryRow {
  customer_id: string
  is_default: boolean
  address_lines: string[] | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string | null
}

function formatTypedAddress(a: CustomerAddressQueryRow): string | null {
  // address_lines can carry blank slots (an in-progress edit, or a legacy
  // migrated row) -- filter those out before joining, or a blank slot between
  // two filled lines shows up as a double comma.
  const lines = (a.address_lines ?? []).filter(l => l && l.trim().length > 0)
  const parts = [...lines, a.city, a.county, a.postcode, a.country && a.country !== 'GB' ? a.country : null]
    .filter((p): p is string => !!p?.trim())
  return parts.length > 0 ? parts.join(', ') : null
}

// One customer can have several operations rows -- prefer the default one.
function pickOperationsAddress(rows: CustomerAddressQueryRow[], customerId: string | null): string | null {
  if (!customerId) return null
  const forCustomer = rows.filter(r => r.customer_id === customerId)
  const row = forCustomer.find(r => r.is_default) ?? forCustomer[0]
  return row ? formatTypedAddress(row) : null
}

interface ShippingTermsRow {
  shipping_terms_id: string | null
}

// Determines whether the input job_ref is a plain job, a CONSOL parent (in
// which case its children are auto-included as one bundle), or a child job
// entered directly (in which case it is checked alone, never expanded to
// its siblings). Children carry their own job_goods rows that duplicate the
// parent's own master-level totals (see create-autojobs), so a consol
// bundle sums job_goods from the children only, not the parent.
export async function resolveJobBundle(jobRef: string): Promise<JobBundle> {
  const { data: job, error } = await supabase
    .from('jobs')
    .select('id, job_ref, transport_mode, job_type, parent_job_id, importer_id, consignor_id, hbl_importer_id, hbl_consignor_id')
    .eq('job_ref', jobRef)
    .single()

  if (error || !job) {
    throw new Error(`Job not found for job_ref "${jobRef}"`)
  }

  if (job.parent_job_id) {
    return { primaryJob: job, goodsJobIds: [job.id], isConsolBundle: false, childJobRefs: [] }
  }

  if (job.job_type === 'CONSOL') {
    const { data: children } = await supabase
      .from('jobs')
      .select('id, job_ref')
      .eq('parent_job_id', job.id)
      .order('job_ref_index', { ascending: true })

    if (children && children.length > 0) {
      return {
        primaryJob: job,
        goodsJobIds: children.map(c => c.id),
        isConsolBundle: true,
        childJobRefs: children.map(c => c.job_ref),
      }
    }
  }

  return { primaryJob: job, goodsJobIds: [job.id], isConsolBundle: false, childJobRefs: [] }
}

export async function fetchJobVerificationData(bundle: JobBundle): Promise<JobVerificationData> {
  const job = bundle.primaryJob
  // Which party set does this job declare? ONE rule shared with the declaration, the invoice PDF and the BoxTop sync
  // (supabase/functions/_shared/partyScope.ts): a consol child or a one-master-one-house job uses its HBL parties, a consol master
  // or any other job the plain ones. (This used to be "hbl || plain", which also let a consol MASTER's HBL fields win.)
  const detailsTable = job.transport_mode === 'SEA' ? 'job_sea_details' : job.transport_mode === 'AIR' ? 'job_air_details' : null
  const detailsColumns = job.transport_mode === 'SEA' ? 'shipping_terms_id, master_bl_no, house_bl_no' : 'shipping_terms_id, mawb_no, hawb_no'
  const detailsResult = detailsTable
    ? await supabase.from(detailsTable).select(detailsColumns).eq('job_id', job.id).maybeSingle()
    : { data: null, error: null }
  const details = detailsResult.data as (ShippingTermsRow & { master_bl_no?: string | null; house_bl_no?: string | null; mawb_no?: string | null; hawb_no?: string | null }) | null
  const flags = docFlagsOf(job.transport_mode === 'SEA' ? details : null, job.transport_mode === 'AIR' ? details : null)
  const effectiveImporterId = pickParty(job, 'importer', flags).id
  const effectiveConsignorId = pickParty(job, 'consignor', flags).id
  const customerIds = [effectiveImporterId, effectiveConsignorId].filter(Boolean) as string[]

  const [customersResult, addressesResult, jobGoodsResult] = await Promise.all([
    customerIds.length > 0
      ? supabase.from('customers').select(CUSTOMER_COLUMNS).in('id', customerIds)
      : Promise.resolve({ data: [], error: null }),
    customerIds.length > 0
      ? supabase.from('customer_addresses').select(ADDRESS_COLUMNS).in('customer_id', customerIds).eq('address_type', 'operations')
      : Promise.resolve({ data: [], error: null }),
    supabase
      .from('job_goods')
      .select('goods_description, no_of_packs, gross_weight, volume_m3')
      .in('job_id', bundle.goodsJobIds),
  ])

  if (customersResult.error) {
    throw new Error(`Failed to look up job's consignor/importer: ${customersResult.error.message}`)
  }

  const customers = (customersResult.data ?? []) as CustomerQueryRow[]
  const addresses = (addressesResult.data ?? []) as CustomerAddressQueryRow[]
  const importerRow = customers.find(c => c.id === effectiveImporterId) ?? null
  const consignorRow = customers.find(c => c.id === effectiveConsignorId) ?? null
  // Typed operations address wins where present; customers.address (via
  // normalizeCustomerFacets/formatCustomerAddress) is the fallback when there is no typed row.
  const importerTypedAddress = pickOperationsAddress(addresses, effectiveImporterId)
  const consignorTypedAddress = pickOperationsAddress(addresses, effectiveConsignorId)

  let shippingTermsCode: string | null = null
  const shippingTermsId = details?.shipping_terms_id
  if (shippingTermsId) {
    const { data: termsRow } = await supabase
      .from('shipping_terms')
      .select('code')
      .eq('id', shippingTermsId)
      .single()
    shippingTermsCode = termsRow?.code ?? null
  }

  const jobGoods = jobGoodsResult.data ?? []
  const goodsWithVolume = jobGoods.filter((g: any) => g.volume_m3 != null)
  const totals = {
    packages: jobGoods.reduce((sum, g) => sum + (g.no_of_packs ?? 0), 0),
    grossWeight: jobGoods.reduce((sum, g) => sum + (g.gross_weight ?? 0), 0),
    cbm: goodsWithVolume.length > 0
      ? goodsWithVolume.reduce((sum: number, g: any) => sum + (g.volume_m3 ?? 0), 0)
      : null,
  }

  return {
    job: { id: job.id, job_ref: job.job_ref, transport_mode: job.transport_mode },
    consignor: consignorRow ? { ...normalizeCustomerFacets(consignorRow), address: consignorTypedAddress ?? normalizeCustomerFacets(consignorRow).address } : null,
    importer: importerRow ? { ...normalizeCustomerFacets(importerRow), address: importerTypedAddress ?? normalizeCustomerFacets(importerRow).address } : null,
    shippingTermsCode,
    jobGoods,
    totals,
  }
}
